/**
 * `useBranding` + `DynamicLogo`
 *
 * Phase E (May 6, 2026): canonical home moved to
 * `shared/hooks/useBranding.jsx`. The legacy path
 * `@/hooks/useBranding` continues to work via a thin re-export.
 *
 * Behavior unchanged:
 *   - Module-level cache keyed by organizationId so two orgs in
 *     adjacent tabs can't cross-pollute each other's branding.
 *   - Re-fetches on `authChanged`, `brandingUpdated`, `storage` events.
 *   - Document title + favicon kept in sync with cache-busting.
 *
 * Phase C alignment: `API_URL` now sourced from `@/core/env`.
 */
import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '@/core/env';

// Default fallback company name
const DEFAULT_COMPANY_NAME = 'EOC Closet';

// Read the current user's organizationId from the zustand-persisted auth state.
// Storage key is maintained by /src/store/useStore.js (`eoc-current-user`).
// Falling back to anonymous mode (=> default org) when no user is logged in.
const _readCallerOrgId = () => {
  try {
    const raw = localStorage.getItem('eoc-current-user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u && u.organizationId ? String(u.organizationId) : null;
  } catch {
    return null;
  }
};

// ============================================================
// Module-level cache — keyed by organizationId so two orgs in
// adjacent tabs can't cross-pollute each other's branding.
// ============================================================
const _brandingCacheByOrg = new Map(); // orgId|'' -> {companyName, logoUrl, iconUrl, _ts}
const _fetchPromiseByOrg = new Map();

const _defaultCache = () => ({
  companyName: DEFAULT_COMPANY_NAME,
  logoUrl: '',
  iconUrl: '',
  _ts: 0,
});

// Update document title
const updateDocumentTitle = (companyName) => {
  document.title = companyName || DEFAULT_COMPANY_NAME;
};

// Update favicon dynamically with cache-busting
const updateFavicon = (iconUrl) => {
  // Remove existing favicons
  const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
  existingFavicons.forEach(el => el.remove());

  // Add cache-buster to force browser refresh
  const cacheBuster = `v=${Date.now()}`;
  const separator = iconUrl.includes('?') ? '&' : '?';
  const freshUrl = `${iconUrl}${separator}${cacheBuster}`;

  // Add new favicon
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/png';
  link.href = freshUrl;
  document.head.appendChild(link);

  // Also add apple-touch-icon
  const appleLink = document.createElement('link');
  appleLink.rel = 'apple-touch-icon';
  appleLink.href = freshUrl;
  document.head.appendChild(appleLink);
};

// Shared fetch function — deduplicates concurrent calls PER ORG
async function _fetchBrandingData(orgId) {
  const key = orgId || '';
  if (_fetchPromiseByOrg.has(key)) return _fetchPromiseByOrg.get(key);
  const promise = (async () => {
    try {
      const qs = orgId ? `?organizationId=${encodeURIComponent(orgId)}` : '';
      const response = await fetch(`${API_URL}/api/public/settings/branding${qs}`);
      if (response.ok) {
        const data = await response.json();
        const companyName = data.companyName || DEFAULT_COMPANY_NAME;
        const ts = Date.now();

        _brandingCacheByOrg.set(key, {
          companyName,
          logoUrl: data.logoUrl || '',
          iconUrl: data.iconUrl || '',
          _ts: ts,
        });

        updateDocumentTitle(companyName);
        if (data.iconUrl) {
          updateFavicon(`${API_URL}${data.iconUrl}`);
        }
      }
    } catch (error) {
      console.warn('Could not load branding settings');
      updateDocumentTitle(DEFAULT_COMPANY_NAME);
    } finally {
      _fetchPromiseByOrg.delete(key);
    }
    return _brandingCacheByOrg.get(key) || _defaultCache();
  })();
  _fetchPromiseByOrg.set(key, promise);
  return promise;
}

// Custom hook to fetch and use branding settings
export const useBranding = () => {
  // Track which org this hook instance is bound to so we can refetch on login/logout
  const [orgKey, setOrgKey] = useState(() => _readCallerOrgId() || '');
  const [branding, setBranding] = useState(() =>
    _brandingCacheByOrg.get(orgKey) || _defaultCache()
  );
  const [isLoading, setIsLoading] = useState(() => {
    const c = _brandingCacheByOrg.get(orgKey);
    return !(c && (c.iconUrl || c.logoUrl));
  });

  const fetchBranding = useCallback(async (key = orgKey) => {
    const data = await _fetchBrandingData(key);
    setBranding({ ...data });
    setIsLoading(false);
  }, [orgKey]);

  useEffect(() => {
    // Re-read the caller org every time this effect runs — handles login/logout
    const currentOrg = _readCallerOrgId() || '';
    if (currentOrg !== orgKey) {
      setOrgKey(currentOrg);
      return; // effect will re-run with the new key
    }
    fetchBranding(currentOrg);

    // Listen for branding updates pushed from the admin panel
    const handleBrandingUpdate = () => {
      // Invalidate cache for the current org then refetch
      _brandingCacheByOrg.delete(currentOrg);
      fetchBranding(currentOrg);
    };
    // Listen for auth changes so we re-resolve branding for the new user's org
    const handleAuthChanged = () => {
      const nextOrg = _readCallerOrgId() || '';
      setOrgKey(nextOrg);
    };

    window.addEventListener('brandingUpdated', handleBrandingUpdate);
    window.addEventListener('authChanged', handleAuthChanged);
    window.addEventListener('storage', handleAuthChanged);
    return () => {
      window.removeEventListener('brandingUpdated', handleBrandingUpdate);
      window.removeEventListener('authChanged', handleAuthChanged);
      window.removeEventListener('storage', handleAuthChanged);
    };
  }, [orgKey, fetchBranding]);

  return { branding, isLoading, refetch: () => fetchBranding(orgKey) };
};

// Component that renders the dynamic logo
export const DynamicLogo = ({ showText = true, iconOnly = false, className = '' }) => {
  const { branding } = useBranding();

  // Cache-bust logo and icon URLs. The URL from the server already includes
  // `?organizationId=...` so we use `&` as the separator here.
  const sep = (u) => (u.includes('?') ? '&' : '?');
  const cacheBustLogo = branding._ts ? `${sep(branding.logoUrl || '')}v=${branding._ts}` : '';
  const cacheBustIcon = branding._ts ? `${sep(branding.iconUrl || '')}v=${branding._ts}` : '';

  // If there's a logo URL, use it
  if (branding.logoUrl && !iconOnly) {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <img
          src={`${API_URL}${branding.logoUrl}${cacheBustLogo}`}
          alt={branding.companyName}
          className="h-10 sm:h-12 max-w-[180px] object-contain"
        />
        {showText && (
          <>
            <div className="hidden sm:block border-l border-slate-600 h-8 mx-2" />
            <span className="hidden sm:block text-white font-bold text-lg">{branding.companyName}</span>
          </>
        )}
      </div>
    );
  }

  // If there's an icon URL and iconOnly mode
  if (branding.iconUrl && iconOnly) {
    return (
      <img
        src={`${API_URL}${branding.iconUrl}${cacheBustIcon}`}
        alt={branding.companyName}
        className={`h-8 w-8 object-contain ${className}`}
      />
    );
  }

  // No fallback - return null if no icon is set
  return null;
};

export default useBranding;
